import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";

/**
 * Form data interface
 * Represents the complete form configuration and state
 */
interface FormData {
  type: string;
  data: any;
  submitted?: any;
  fields?: any[];
  validation?: any;
  api?: {
    url: string;
    processType: number;
    operationType: number;
    method?: string;
    headers?: Record<string, string>;
  };
}

/**
 * Result data interface
 * Represents the API response and loading states
 */
interface ResultData {
  response: any;
  loading: boolean;
  statusCode: number | null;
  justResult?: boolean;
  userDriven?: boolean;
}

/**
 * Form context interface
 * Provides form state management capabilities
 */
interface FormContextType {
  formData: FormData;
  setFormData: (data: FormData) => void;
  updateFormData: (updates: Partial<FormData>) => void;
}

/**
 * Result context interface
 * Provides result state management capabilities
 */
interface ResultContextType {
  resultData: ResultData;
  setResultData: (data: ResultData) => void;
  updateResultData: (updates: Partial<ResultData>) => void;
}

/**
 * Form Context
 * Manages form configuration and submission state
 */
const FormContext = createContext<FormContextType | undefined>(undefined);

/**
 * Result Context
 * Manages API response and loading states
 */
const ResultContext = createContext<ResultContextType | undefined>(undefined);

/**
 * Form Provider Component
 * Provides form state management to child components
 *
 * @param children - Child components that need access to form state
 */
export const FormProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [formData, setFormData] = useState<FormData>({
    type: "",
    data: null,
  });

  /**
   * Updates form data with partial updates
   * Memoized to prevent infinite loops in useEffect dependencies
   *
   * @param updates - Partial form data to merge with current state
   */
  const updateFormData = useCallback((updates: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <FormContext.Provider value={{ formData, setFormData, updateFormData }}>
      {children}
    </FormContext.Provider>
  );
};

/**
 * Result Provider Component
 * Provides result state management to child components
 *
 * @param children - Child components that need access to result state
 */
export const ResultProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [resultData, setResultData] = useState<ResultData>({
    response: null,
    loading: false,
    statusCode: null,
    justResult: false,
  });

  /**
   * Updates result data with partial updates
   * Memoized to prevent infinite loops in useEffect dependencies
   *
   * @param updates - Partial result data to merge with current state
   */
  const updateResultData = useCallback((updates: Partial<ResultData>) => {
    setResultData((prev) => ({ ...prev, ...updates }));
  }, []);

  return (
    <ResultContext.Provider
      value={{ resultData, setResultData, updateResultData }}
    >
      {children}
    </ResultContext.Provider>
  );
};

/**
 * Combined Simulator Provider
 * Wraps both Form and Result providers for complete state management
 *
 * @param children - Child components that need access to both form and result state
 */
export const SimulatorProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  return (
    <FormProvider>
      <ResultProvider>{children}</ResultProvider>
    </FormProvider>
  );
};

/**
 * Custom hook to access Form Context
 *
 * @throws Error if used outside of FormProvider
 * @returns Form context value with state and updaters
 */
export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

/**
 * Custom hook to access Result Context
 *
 * @throws Error if used outside of ResultProvider
 * @returns Result context value with state and updaters
 */
export const useResultContext = () => {
  const context = useContext(ResultContext);
  if (!context) {
    throw new Error("useResultContext must be used within a ResultProvider");
  }
  return context;
};
